import LiveKitActionsBarContainer from './LiveKitActionsBarContainer';
import LiveKitCallHeader from './LiveKitCallHeader';
import LiveKitLocalPreview from './LiveKitLocalPreview';
import useAnnounceReady from './useAnnounceReady';
import useAudioOutputPreference from './useAudioOutputPreference';
import useDisconnectOnUnload from './useDisconnectOnUnload';
import useDoctorPresence from './useDoctorPresence';
import usePublishLocalTracks from './usePublishLocalTracks';

import TelemedicineContext from '../../../context/telemedicine';
import { formatUserName } from '../../../utils/utils';

import { Flex, Icon, Loading, Text } from '@amigoapp/doca-react';
import { RoomAudioRenderer, useConnectionState, useTracks, VideoTrack } from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';
import { useContext } from 'react';

const LiveKitVideoRoom = () => {
	const previewVideoTrack = usePublishLocalTracks();

	useDisconnectOnUnload();
	useAnnounceReady();
	useAudioOutputPreference();

	const { telemedicine } = useContext(TelemedicineContext);

	const { isDoctorConnectionLost } = useDoctorPresence();

	const connectionState = useConnectionState();
	const loading = connectionState !== ConnectionState.Connected;

	const cameras = useTracks([Track.Source.Camera]);
	const localCamera = cameras.find(camera => camera.participant.isLocal);
	const remoteCamera = cameras.find(camera => !camera.participant.isLocal);

	const renderStage = () => {
		// A camera track that is still delivering frames is proof the doctor is connected,
		// whatever any presence signal claims — so it is checked first and on its own.
		if (remoteCamera) {
			return <VideoTrack className="livekit-video" trackRef={remoteCamera} />;
		}

		if (isDoctorConnectionLost) {
			return (
				<Flex align="center" className="doctor-stage-message" direction="col" gap={3} justify="center">
					<Icon name="wifi-slash" type="light" />
					<Text align="center" weight="bold">O médico perdeu a conexão</Text>
					<Text align="center" size="small" weight="light">
						Aguarde, ele deve retornar em instantes.
					</Text>
				</Flex>
			);
		}

		if (loading) {
			return null;
		}

		return (
			<Flex align="center" className="doctor-stage-message" direction="col" gap={3} justify="center">
				<Loading />
				<Text align="center" size="small" weight="light">Aguardando o vídeo do médico</Text>
			</Flex>
		);
	};

	return (
		<Flex className="lk-call" direction="col" flex={1}>
			{loading && <Loading className="absolute" />}
			<div className="doca-flex doca-flex-1 doca-justify-center" id="doctor-tracks">
				<LiveKitCallHeader providerName={formatUserName(telemedicine.user.title, telemedicine.user.name)} />

				{renderStage()}
			</div>
			<div id="patient-tracks">
				{loading && !previewVideoTrack && <Loading />}
				{localCamera
					? <VideoTrack className="livekit-video" trackRef={localCamera} />
					: previewVideoTrack && <LiveKitLocalPreview track={previewVideoTrack} />}
				<span className="patient-tracks__label">Você</span>
			</div>
			<RoomAudioRenderer />
			<LiveKitActionsBarContainer />
		</Flex>
	);
};

export default LiveKitVideoRoom;
